// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { Action } from 'src/types';
import { ChargesByMoveGroup } from '@features/invoices/services/calcServiceCharges';
import { useGetHistoricalArgroupsLazyQuery } from '../queries/getHistoricalArgroups.query.generated';

import { useCustomerConfig } from '@store';
import { capFirst, clampNegNum, getMonetaryValue } from '@services/formatService';
import { getFormattedVehicleFromMove } from '@services/moveService';

import { css } from '@emotion/css';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Icon,
  IconButton,
  Menu,
  MenuItem,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { BiChevronDown, BiFile } from 'react-icons/bi';

import Spacer from '@components/Utils/Spacer';
import InfoItem from '@components/InfoItem';

import MoveDetailsTimeline from '@features/moves/components/MoveDetailsTimeline';
import GuestRideProviderMark from '@features/guestRides/components/GuestRideProviderMark';
import { SETTLED_PAYMENT_STATUSES, settledAllocationTotal } from '@hopdrive/sdk-ar';
import { AMOUNT_DUE_UNAVAILABLE_TEXT, useMoveAmountDues } from '@features/invoices/hooks/useInvoiceAmountDue';
import { AMOUNT_DUE_LOADING_TEXT } from '@features/invoices/services/calcServiceCharges';

// TYPES ---------------------------------------------------------------- //

type InvoiceDetailsTableFoldChargesProps = {
  moveGroup: ChargesByMoveGroup;
  actions?: Action[];
  invoice?: any;
};

// COMPONENT ---------------------------------------------------------------- //

const InvoiceDetailsTableFoldCharges = ({ moveGroup, actions, invoice }: InvoiceDetailsTableFoldChargesProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const clsx = useSxStyles(theme);

  const isRideshareInvoice = invoice?.invoice_type?.includes('rideshare');

  const customerConfig = useCustomerConfig();
  const refLabel = customerConfig?.reference_num_label || 'Reference ID';

  const isGuestrideGroup = moveGroup.sourceKind === 'guestride';
  const guestride = moveGroup.guestride || null;
  const move = moveGroup.move || {};
  const pickup = move?.lane?.pickup || {};
  const delivery = move?.lane?.delivery || {};

  // Get child move (ride) details if rideshare
  const childMove = move?.childMoves?.[0];
  const ridePickup = childMove?.lane?.pickup || {};
  const rideDelivery = childMove?.lane?.delivery || {};
  // For guestride groups, the ridehail lives directly on the guestride.
  const ridehailData = isGuestrideGroup
    ? guestride?.ridehails?.[0] || null
    : childMove?.ridehails?.[0] || null;

  const [actionsOpen, setActionsOpen] = React.useState<null | HTMLElement>(null);
  const [historicalArgroups, setHistoricalArgroups] = React.useState<any[]>([]);
  const [revisionsExpanded, setRevisionsExpanded] = React.useState(false);

  const [fetchHistoricalArgroups, { loading: loadingHistorical }] = useGetHistoricalArgroupsLazyQuery();

  const handleActionsOpen = (event?: React.MouseEvent<HTMLElement>) => {
    setActionsOpen(event?.currentTarget || null);
  };
  const handleActionsClose = () => {
    setActionsOpen(null);
  };

  const handleAction = (action?: Action) => {
    handleActionsClose();
    if (action?.handler) action?.handler();
  };

  // Fetch historical argroups when revisions accordion is expanded
  const handleRevisionsExpand = async (_event: React.SyntheticEvent, expanded: boolean) => {
    setRevisionsExpanded(expanded);
    if (expanded && historicalArgroups.length === 0 && moveGroup.scenarioId && invoice?.id) {
      const result = await fetchHistoricalArgroups({
        variables: {
          invoiceId: invoice.id,
          scenarioId: moveGroup.scenarioId,
          settledPaymentStatuses: [...SETTLED_PAYMENT_STATUSES],
        },
      });
      if (result.data?.argroups) {
        setHistoricalArgroups(result.data.argroups);
      }
    }
  };

  const getRateClass = () => {
    // Get from transport charge notes field (format: "Class 1, $15.00 flat")
    const transportCharge = moveGroup.charges.find((c: any) => c.type === 'move' || c.name === 'Transport' || c.name?.includes('Transport'));
    if (transportCharge?.notes) {
      // Extract just the class name before the comma
      return transportCharge.notes.split(',')[0];
    }

    return 'N/A';
  };

  const getProviderChip = (provider: string) => {
    const lowerProvider = provider?.toLowerCase() || '';
    if (lowerProvider.includes('uber') || lowerProvider.includes('lyft')) {
      return <GuestRideProviderMark provider={provider} />;
    }
    return <Typography sx={{ fontSize: '14px' }}>{capFirst(provider)}</Typography>;
  };

  // Build line items from charges
  let lineItems = moveGroup.charges.map((charge: any) => {
    let name = charge.name || 'Charge';
    let notes = charge.notes ? capFirst(charge.notes) : null;

    if (isGuestrideGroup) {
      // Simplify guestride charge names and drop the verbose source notes
      if (/platform fee/i.test(name)) {
        name = 'Platform Fee';
      } else if (/ride/i.test(name)) {
        name = 'Ride';
      } else {
        name = capFirst(name);
      }
      notes = null;
    } else if (name.includes('INVALID')) {
      // Format charge name
      name = 'INVALID Transport Cost';
    } else if (name.includes('Transport') || charge.type === 'move') {
      name = 'Transport Cost';
    } else if (!name.includes('Promo') && !name.includes('promo') && charge.source_type === 'accessorial') {
      name = `Accessorial: ${capFirst(name)}`;
    } else {
      name = capFirst(name);
    }

    const amount = charge.amount < 0 ? `(${getMonetaryValue(Math.abs(charge.amount))})` : getMonetaryValue(charge.amount);

    return { name, notes, amount };
  });

  // Sort line items
  lineItems = lineItems.sort((a, b) => {
    if (a.name < b.name) return -1;
    if (a.name > b.name) return 1;
    return 0;
  });

  // The row's charges less its credits, as sdk-ar's grouping totals them.
  const total = moveGroup.total;

  const paidAmount = settledAllocationTotal(moveGroup.charges.flatMap((charge) => charge.arpaymentdetails ?? []));

  // The move's Amount Due is sdk-ar's answer over its charges, the answer a
  // payment on them collects.
  const [due] = useMoveAmountDues([moveGroup]);
  const dueText =
    due.status === 'ready'
      ? getMonetaryValue(clampNegNum(due.amount))
      : due.status === 'loading'
        ? AMOUNT_DUE_LOADING_TEXT
        : AMOUNT_DUE_UNAVAILABLE_TEXT;
  const statusText =
    due.status === 'ready'
      ? due.amount <= 0
        ? `Paid`
        : paidAmount <= 0
          ? `Unpaid`
          : `Partial`
      : dueText;

  return (
    <div className={cls.fold}>
      {isGuestrideGroup ? (
        <>
          {/* Guestride Pickup/Dropoff Column */}
          <div className={cls.foldRideshareLeft}>
            <Typography sx={clsx.titleText} style={{ marginBottom: `12px` }}>
              Pickup
            </Typography>
            <Typography sx={{ fontSize: '12px', color: theme?.palette?.text?.secondary, marginBottom: '12px' }}>
              {guestride?.pickup_address || 'N/A'}
            </Typography>
            <div
              style={{
                height: '4px',
                backgroundColor: '#ff4444',
                marginBottom: '24px',
                borderRadius: '2px',
              }}
            />

            <Typography sx={clsx.titleText} style={{ marginBottom: `12px` }}>
              Dropoff
            </Typography>
            <Typography sx={{ fontSize: '12px', color: theme?.palette?.text?.secondary, marginBottom: '12px' }}>
              {guestride?.dropoff_address || 'N/A'}
            </Typography>
            <div
              style={{
                height: '4px',
                backgroundColor: '#ff4444',
                marginBottom: '12px',
                borderRadius: '2px',
              }}
            />
          </div>

          {/* Guest & Rideshare Details Column */}
          <div className={cls.foldRideshareMiddle}>
            <Typography sx={clsx.titleText} style={{ marginBottom: `16px` }}>
              Guest Details
            </Typography>

            <div className={cls.dottedLineItem}>
              <Typography sx={{ fontSize: '14px', color: theme?.palette?.text?.secondary }}>HopDrive ID</Typography>
              <div className={cls.dottedLine} />
              <Typography sx={{ fontSize: '14px' }}>{guestride?.id ? `R-#${guestride.id}` : `N/A`}</Typography>
            </div>

            <div className={cls.dottedLineItem} style={{ marginTop: '8px' }}>
              <Typography sx={{ fontSize: '14px', color: theme?.palette?.text?.secondary }}>Guest Name</Typography>
              <div className={cls.dottedLine} />
              <Typography sx={{ fontSize: '14px' }}>{guestride?.guest_name || `N/A`}</Typography>
            </div>

            <div className={cls.dottedLineItem} style={{ marginTop: '8px' }}>
              <Typography sx={{ fontSize: '14px', color: theme?.palette?.text?.secondary }}>{refLabel}</Typography>
              <div className={cls.dottedLine} />
              <Typography sx={{ fontSize: '14px' }}>{guestride?.reference_num || `N/A`}</Typography>
            </div>

            <Spacer size='lg' />

            {ridehailData && (
              <>
                <Typography sx={clsx.titleText} style={{ marginBottom: `16px` }}>
                  Rideshare Details
                </Typography>

                <div className={cls.dottedLineItem}>
                  <Typography sx={{ fontSize: '14px', color: theme?.palette?.text?.secondary }}>Provider</Typography>
                  <div className={cls.dottedLine} />
                  <div style={{ display: 'flex', alignItems: 'center' }}>{getProviderChip(ridehailData.provider)}</div>
                  <Typography sx={{ fontSize: '14px', color: theme?.palette?.text?.secondary, marginLeft: '16px' }}>
                    Product Type
                  </Typography>
                  <div className={cls.dottedLine} />
                  <Typography sx={{ fontSize: '14px' }}>{capFirst(ridehailData.product_type) || `N/A`}</Typography>
                </div>

                <div className={cls.dottedLineItem} style={{ marginTop: '8px' }}>
                  <Typography sx={{ fontSize: '14px', color: theme?.palette?.text?.secondary, lineHeight: 1.2 }}>
                    Ride ID
                  </Typography>
                  <div className={cls.dottedLine} />
                  <Typography sx={{ fontSize: '14px' }}>{ridehailData.external_id || `N/A`}</Typography>
                </div>
              </>
            )}
          </div>
        </>
      ) : isRideshareInvoice ? (
        <>
          {/* Rideshare Pickup/Dropoff Column */}
          <div className={cls.foldRideshareLeft}>
            <Typography sx={clsx.titleText} style={{ marginBottom: `12px` }}>
              Rideshare Pickup
            </Typography>
            <Typography sx={{ fontSize: '14px', marginBottom: '8px' }}>{ridePickup?.name || 'N/A'}</Typography>
            <Typography sx={{ fontSize: '12px', color: theme?.palette?.text?.secondary, marginBottom: '12px' }}>
              {ridePickup?.address || 'N/A'}
            </Typography>
            <div
              style={{
                height: '4px',
                backgroundColor: '#ff4444',
                marginBottom: '24px',
                borderRadius: '2px',
              }}
            />

            <Typography sx={clsx.titleText} style={{ marginBottom: `12px` }}>
              Rideshare Dropoff
            </Typography>
            <Typography sx={{ fontSize: '14px', marginBottom: '8px' }}>{rideDelivery?.name || 'N/A'}</Typography>
            <Typography sx={{ fontSize: '12px', color: theme?.palette?.text?.secondary, marginBottom: '12px' }}>
              {rideDelivery?.address || 'N/A'}
            </Typography>
            <div
              style={{
                height: '4px',
                backgroundColor: '#ff4444',
                marginBottom: '12px',
                borderRadius: '2px',
              }}
            />
          </div>

          {/* Parent Drive & Rideshare Details Column */}
          <div className={cls.foldRideshareMiddle}>
            <Typography sx={clsx.titleText} style={{ marginBottom: `16px` }}>
              Parent Drive Details
            </Typography>

            <div className={cls.dottedLineItem}>
              <Typography sx={{ fontSize: '14px', color: theme?.palette?.text?.secondary }}>Vehicle</Typography>
              <div className={cls.dottedLine} />
              <Typography sx={{ fontSize: '14px' }}>{getFormattedVehicleFromMove(move) || `N/A`}</Typography>
            </div>

            <div className={cls.dottedLineItem} style={{ marginTop: '8px' }}>
              <Typography sx={{ fontSize: '14px', color: theme?.palette?.text?.secondary }}>{refLabel}</Typography>
              <div className={cls.dottedLine} />
              <Typography sx={{ fontSize: '14px' }}>{move?.reference_num || `N/A`}</Typography>
            </div>

            <div className={cls.dottedLineItem} style={{ marginTop: '8px' }}>
              <Typography sx={{ fontSize: '14px', color: theme?.palette?.text?.secondary }}>VIN</Typography>
              <div className={cls.dottedLine} />
              <Typography sx={{ fontSize: '14px' }}>{move?.vehicle_vin || `N/A`}</Typography>
            </div>

            <Spacer size='lg' />

            {ridehailData && (
              <>
                <Typography sx={clsx.titleText} style={{ marginBottom: `16px` }}>
                  Rideshare Details
                </Typography>

                <div className={cls.dottedLineItem}>
                  <Typography sx={{ fontSize: '14px', color: theme?.palette?.text?.secondary }}>Provider</Typography>
                  <div className={cls.dottedLine} />
                  <div style={{ display: 'flex', alignItems: 'center' }}>{getProviderChip(ridehailData.provider)}</div>
                  <Typography sx={{ fontSize: '14px', color: theme?.palette?.text?.secondary, marginLeft: '16px' }}>
                    Product Type
                  </Typography>
                  <div className={cls.dottedLine} />
                  <Typography sx={{ fontSize: '14px' }}>{capFirst(ridehailData.product_type) || `N/A`}</Typography>
                </div>

                <div className={cls.dottedLineItem} style={{ marginTop: '8px' }}>
                  <Typography sx={{ fontSize: '14px', color: theme?.palette?.text?.secondary, lineHeight: 1.2 }}>
                    Ride ID
                  </Typography>
                  <div className={cls.dottedLine} />
                  <Typography sx={{ fontSize: '14px' }}>{ridehailData.external_id || `N/A`}</Typography>
                </div>
              </>
            )}
          </div>
        </>
      ) : (
        <div className={cls.foldLeft}>
          <MoveDetailsTimeline move={move} mb={12} showTimes={false} />

          <div className={cls.foldLeftInner}>
            <Typography sx={clsx.titleText} style={{ marginBottom: `12px` }}>
              Move Info
            </Typography>

            <div className={cls.sectionFlex}>
              <div className={cls.sectionFlexItem}>
                <InfoItem size='sm' keyText={`HopDrive ID`} valueText={move?.id ? `M-#${move?.id}` : `N/A`} />
                <InfoItem size='sm' keyText={`VIN`} valueText={move?.vehicle_vin || `N/A`} />
              </div>

              <div className={cls.sectionFlexItem}>
                <InfoItem size='sm' keyText={refLabel} valueText={move?.reference_num || `N/A`} />
                <InfoItem size='sm' keyText={`Stock`} valueText={move?.vehicle_stock || `N/A`} />
              </div>
            </div>

            <Spacer size={0.5} />

            <InfoItem size='sm' keyText={`Vehicle`} valueText={getFormattedVehicleFromMove(move) || `N/A`} />

            <Spacer />

            <Typography sx={clsx.titleText} style={{ marginBottom: `12px` }}>
              Rate Info
            </Typography>

            <div className={cls.sectionFlex}>
              <div className={cls.sectionFlexItem}>
                <InfoItem size='sm' keyText={`Rate Class`} valueText={getRateClass() || `N/A`} />
              </div>

              <div className={cls.sectionFlexItem}>
                <InfoItem
                  size='sm'
                  keyText={`Rate Rule`}
                  valueText={
                    move?.raterule?.type
                      ? `${move?.raterule?.type === 'per' ? 'Per Mile' : capFirst(move?.raterule?.type)}`
                      : `N/A`
                  }
                />
              </div>
            </div>

            <Spacer />

            <Typography sx={clsx.titleText} style={{ marginBottom: `12px` }}>
              Lane Info
            </Typography>

            <div className={cls.sectionFlex}>
              <div className={cls.sectionFlexItem}>
                <InfoItem size='sm' keyText={`Pickup Address`} valueText={pickup?.address || `N/A`} />
                <InfoItem size='sm' keyText={`Delivery Address`} valueText={delivery?.address || `N/A`} />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={cls.foldRight}>
        <div className={cls.flex}>
          <Typography sx={clsx.titleText}>Charge Details</Typography>

          <Tooltip placement='top' title={`Actions`}>
            <IconButton sx={clsx.iconBtn} onClick={handleActionsOpen}>
              <Icon>more_vert</Icon>
            </IconButton>
          </Tooltip>

          <Menu
            keepMounted
            id={`movegroup-actions-menu`}
            anchorEl={actionsOpen}
            open={Boolean(actionsOpen)}
            onClose={handleActionsClose}
          >
            {actions?.map((action?: Action, i?: number) =>
              !action?.hide ? (
                <MenuItem key={`movegroup-action-${i}`} onClick={() => handleAction(action)} disabled={action?.disabled}>
                  {action?.icon || null}
                  {action?.label || `Action ${i || 0 + 1}`}
                </MenuItem>
              ) : null
            )}
          </Menu>
        </div>

        <Spacer />

        {/* Revisions Section */}
        {(() => {
          // Build revisions from fetched historical argroups
          const allRevisions: any[] = [];
          if (historicalArgroups.length > 0) {
            historicalArgroups.forEach((argroup: any) => {
              const charges = argroup.archarges || [];
              if (charges.length > 0) {
                const revTotal = charges.reduce((sum: number, c: any) => sum + Number(c.amount || 0), 0);
                allRevisions.push({
                  argroupId: argroup.id,
                  createdat: argroup.createdat,
                  charges,
                  credits: [],
                  total: revTotal,
                });
              }
            });
          }

          // Get current total for comparison
          const currentTotal = moveGroup.charges.reduce((sum: number, c: any) => sum + Number(c.amount || 0), 0);

          // Filter out revisions with identical amounts to current or previous revision
          const revisions = allRevisions.filter((revision, index) => {
            // Compare to current version
            if (Math.abs(revision.total - currentTotal) < 0.01) return false;

            // Compare to previous revision (revisions are sorted by createdat desc)
            if (index > 0) {
              const prevRevision = allRevisions[index - 1];
              if (Math.abs(revision.total - prevRevision.total) < 0.01) return false;
            }

            return true;
          });

          // Always show the revisions accordion so users can check for historical data
          return (
            <>
              <Accordion
                sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}
                expanded={revisionsExpanded}
                onChange={handleRevisionsExpand}
              >
                <AccordionSummary
                  expandIcon={<BiChevronDown />}
                  sx={{
                    padding: 0,
                    minHeight: '32px !important',
                    '& .MuiAccordionSummary-content': {
                      margin: '8px 0 !important',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: theme?.palette?.text?.secondary }}>
                    {loadingHistorical ? 'Loading Revisions...' : `View Revisions${revisions.length > 0 ? ` (${revisions.length})` : ''}`}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: '0 0 8px 0' }}>
                  {loadingHistorical ? (
                    <Typography sx={{ fontSize: '14px', color: theme?.palette?.text?.secondary }}>
                      Loading...
                    </Typography>
                  ) : revisions.length === 0 ? (
                    <Typography sx={{ fontSize: '14px', color: theme?.palette?.text?.secondary }}>
                      No revision history available for this move.
                    </Typography>
                  ) : (
                    revisions.map((revision, revIndex) => {
                    const revisionNumber = revIndex + 1;
                    const revCharges = revision.charges || [];
                    const revCredits = revision.credits || [];

                    // Build line items for this revision
                    let revLineItems = revCharges.map((charge: any) => {
                      let name = charge.name || 'Charge';

                      if (name.includes('INVALID')) {
                        name = 'INVALID Transport Cost';
                      } else if (name.includes('Transport') || charge.type === 'move') {
                        name = 'Transport Cost';
                      } else if (!name.includes('Promo') && !name.includes('promo') && charge.source_type === 'accessorial') {
                        name = `Accessorial: ${capFirst(name)}`;
                      } else {
                        name = capFirst(name);
                      }

                      const notes = charge.notes ? capFirst(charge.notes) : null;
                      const amount =
                        charge.amount < 0
                          ? `(${getMonetaryValue(Math.abs(charge.amount))})`
                          : getMonetaryValue(charge.amount);
                  
                      return { name, notes, amount };
                    });

                    revLineItems = revLineItems.sort((a: any, b: any) => {
                      if (a.name < b.name) return -1;
                      if (a.name > b.name) return 1;
                      return 0;
                    });

                    const revChargesTotal = revCharges.reduce((sum: number, c: any) => sum + Number(c.amount || 0), 0);
                    const revCreditsTotal = revCredits.reduce((sum: number, c: any) => sum + Number(c.amount || 0), 0);
                    const revTotal = revChargesTotal - revCreditsTotal;

                    return (
                      <div
                        key={`revision-${revision.argroupId}`}
                        style={{
                          marginTop: '8px',
                          padding: '8px',
                          backgroundColor: theme?.palette?.action?.hover,
                          borderRadius: '4px',
                        }}
                      >
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>
                          Revision #{revisionNumber}
                        </Typography>

                        {revLineItems.map((item: any, i: number) => (
                          <React.Fragment key={`rev-${revisionNumber}-charge-${i}`}>
                            <div className={cls.flex}>
                              <Typography sx={clsx.chargeNameTxt} style={{ fontSize: '12px' }}>
                                {item.name}
                              </Typography>
                              <Typography sx={clsx.chargeAmountTxt} style={{ fontSize: '12px' }}>
                                {item.amount}
                              </Typography>
                            </div>

                            {item.notes && (
                              <Typography sx={clsx.chargeNotesTxt} style={{ fontSize: '11px' }}>
                                {item.notes}
                              </Typography>
                            )}

                            {item.documents?.length ? (
                              <div className={cls.docs} style={{ marginTop: '2px' }}>
                                {item.documents.map((doc: any, docIdx: number) => (
                                  <Chip
                                    key={`rev-doc-${i}-${docIdx}`}
                                    size='small'
                                    icon={<BiFile />}
                                    label={doc?.name || 'Document'}
                                    onClick={() => window.open(doc?.url || '/', '_blank')}
                                    sx={{
                                      height: '16px',
                                      backgroundColor: theme?.palette?.info?.main,
                                      color: theme?.palette?.info?.contrastText,
                                      cursor: 'pointer',
                                      fontSize: '10px',
                                    }}
                                  />
                                ))}
                              </div>
                            ) : null}

                            <div className={cls.lineBreak} />
                          </React.Fragment>
                        ))}

                        {revCredits.length > 0 &&
                          revCredits.map((credit: any, i: number) => (
                            <React.Fragment key={`rev-${revisionNumber}-credit-${i}`}>
                              <div className={cls.flex}>
                                <Typography sx={clsx.chargeNameTxt} style={{ fontSize: '12px' }}>
                                  Credit
                                </Typography>
                                <Typography sx={clsx.chargeAmountTxt} style={{ fontSize: '12px' }}>
                                  ({getMonetaryValue(credit.amount)})
                                </Typography>
                              </div>
                              <Typography sx={clsx.chargeNotesTxt} style={{ fontSize: '11px' }}>
                                {capFirst(credit.reason)}
                              </Typography>
                              <div className={cls.lineBreak} />
                            </React.Fragment>
                          ))}

                        <div className={cls.flex} style={{ marginTop: '4px' }}>
                          <Typography sx={{ fontSize: '11px', fontWeight: 500 }}>Total:</Typography>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                            {getMonetaryValue(clampNegNum(revTotal))}
                          </Typography>
                        </div>
                      </div>
                    );
                  })
                  )}
                </AccordionDetails>
              </Accordion>

              <Spacer size='sm' />
            </>
          );
        })()}

        {lineItems?.length
          ? lineItems?.map((item?: any, i?: number) => (
              <React.Fragment key={`charge-detail-${i}`}>
                <div className={cls.flex}>
                  <Typography sx={clsx.chargeNameTxt}>{item?.name}</Typography>
                  <Typography sx={clsx.chargeAmountTxt}>{item?.amount}</Typography>
                </div>

                {item?.notes && <Typography sx={clsx.chargeNotesTxt}>{item?.notes}</Typography>}

                {item?.documents?.length ? (
                  <div className={cls.docs}>
                    {item?.documents?.map((doc?: any, docIdx?: number) => (
                      <Chip
                        key={`doc-${i}-${docIdx}`}
                        size='small'
                        icon={<BiFile />}
                        label={doc?.name || `Document`}
                        onClick={() => window.open(doc?.url || `/`, '_blank')}
                        sx={{
                          height: `18px`,
                          backgroundColor: theme?.palette?.info?.main,
                          color: theme?.palette?.info?.contrastText,
                          cursor: `pointer`,
                          userSelect: `none`,
                          '&:hover': {
                            backgroundColor: theme?.palette?.info?.dark,
                          },
                          '& .MuiChip-icon': {
                            fontSize: `12px`,
                            color: theme?.palette?.info?.contrastText,
                          },
                          '& .MuiChip-label': {
                            fontSize: `12px`,
                            color: theme?.palette?.info?.contrastText,
                          },
                        }}
                      />
                    ))}
                  </div>
                ) : null}

                <div className={cls.lineBreak} />
              </React.Fragment>
            ))
          : null}

        {moveGroup.credits?.length > 0 ? (
          <>
            {moveGroup.credits.map((credit: any, i: number) => (
              <React.Fragment key={`credit-${i}`}>
                <div className={cls.flex}>
                  <Typography sx={clsx.chargeNameTxt}>Credit</Typography>
                  <Typography sx={clsx.chargeAmountTxt}>({getMonetaryValue(credit.amount)})</Typography>
                </div>

                <Typography sx={clsx.chargeNotesTxt}>{capFirst(credit.reason)}</Typography>

                <div className={cls.lineBreak} />
              </React.Fragment>
            ))}
          </>
        ) : null}

        {lineItems?.length || moveGroup.credits?.length ? <Spacer size='xs' /> : null}

        <div className={cls.totals}>
          <div className={cls.totalsLeft}>{/* Reserved for future dispute/notes */}</div>

          <div className={cls.totalsRight}>
            <div className={cls.flex}>
              <Typography sx={clsx.totalsKeyTxt}>Status:</Typography>
              <Typography sx={clsx.totalsValTxt}>{statusText}</Typography>
            </div>

            <Spacer size={1} />

            <div className={cls.flex}>
              <Typography sx={clsx.totalsKeyTxt}>Total:</Typography>
              <Typography sx={clsx.totalsValTxt}>{getMonetaryValue(clampNegNum(total))}</Typography>
            </div>

            <div className={cls.flex}>
              <Typography sx={clsx.totalsKeyTxt}>Amount Paid:</Typography>
              <Typography sx={clsx.totalsValTxt}>{getMonetaryValue(clampNegNum(paidAmount))}</Typography>
            </div>

            <div className={cls.flex}>
              <Typography sx={clsx.totalsKeyTxt}>Amount Due:</Typography>
              <Typography sx={clsx.totalsValTxt}>{dueText}</Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  fold: css`
    position: relative;
    display: flex;
  `,
  foldLeft: css`
    flex: 2;
    padding: 18px 12px 12px 12px;
    border-right: 1px solid ${theme?.palette?.divider};
    background-color: ${theme?.palette?.background?.default};
  `,
  foldLeftInner: css`
    padding: 6px;
  `,
  foldRideshareLeft: css`
    flex: 1;
    padding: 18px;
    border-right: 1px solid ${theme?.palette?.divider};
    background-color: ${theme?.palette?.background?.default};
  `,
  foldRideshareMiddle: css`
    flex: 1;
    padding: 18px;
    border-right: 1px solid ${theme?.palette?.divider};
    background-color: ${theme?.palette?.background?.default};
  `,
  foldRight: css`
    flex: 0 1 360px;
    padding: 18px;
    background-color: ${theme?.palette?.background?.alt};
  `,

  dottedLineItem: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
  dottedLine: css`
    flex: 1;
    border-bottom: 1px dotted ${theme?.palette?.divider};
    min-width: 20px;
  `,

  sectionFlex: css`
    display: flex;
    gap: 20px;
  `,
  sectionFlexItem: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
    word-break: break-word;
  `,

  flex: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  `,

  lineBreak: css`
    border-bottom: 1px solid ${theme?.palette?.divider};
    margin: 4px 0;
  `,

  docs: css`
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
  `,

  totals: css`
    display: flex;
    gap: 12px;
  `,
  totalsLeft: css`
    flex: 1 1;
  `,
  totalsRight: css`
    flex: 0 1 160px;
  `,

  notesBox: css`
    padding: 6px;
    border-radius: ${theme?.shape?.borderRadius}px;
    background-color: ${theme?.palette?.action?.selected};
  `,
});

const useSxStyles = (theme?: Theme) => ({
  titleText: {
    lineHeight: 1,
    fontSize: `16px`,
    fontWeight: `600`,
  },

  iconBtn: {
    padding: theme?.spacing(0.5),
  },

  chargeNameTxt: {
    fontSize: `14px`,
    fontWeight: 500,
  },
  chargeNotesTxt: {
    fontSize: `12px`,
    color: theme?.palette?.text?.secondary,
  },
  chargeAmountTxt: {
    fontSize: `14px`,
    fontWeight: 600,
  },

  notesKeyTxt: {
    fontSize: `12px`,
    fontWeight: 500,
  },
  notesValTxt: {
    fontSize: `12px`,
    color: theme?.palette?.text?.secondary,
  },
  totalsKeyTxt: {
    fontSize: `12px`,
  },
  totalsValTxt: {
    fontSize: `14px`,
    fontWeight: 600,
  },
});

// EXPORTS ---------------------------------------------------------------- //

export default InvoiceDetailsTableFoldCharges;
